import React, { useEffect, useMemo } from 'react';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory, {
  PaginationProvider,
} from 'react-bootstrap-table2-paginator';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  getHandlerTableChange,
  NoRecordsFoundMessage,
  PleaseWaitMessage,
  sortCaret,
  headerSortingClasses,
} from '../../../../../../_metronic/_helpers';
import { injectIntl } from 'react-intl';
import { Pagination } from '../../../../../../_metronic/_partials/controls';
import * as uiHelpers from '../ProductsUIHelpers';
import * as columnFormatters from './column-formatters';
import { useProductsUIContext } from '../ProductsUIContext';
import * as actions from '../../../_redux/products/productsActions';

const ProductsTable = (props) => {
  const { intl } = props;
  const productCodeLabel = intl.formatMessage({
    id: 'PRODUCT.LABEL.PRODUCTCODE',
  });
  const productNameLabel = intl.formatMessage({
    id: 'PRODUCT.LABEL.NAME',
  });
  const categoryLabel = intl.formatMessage({
    id: 'PRODUCT.LABEL.CATEGORY',
  });
  const brandLabel = intl.formatMessage({
    id: 'PRODUCT.LABEL.BRAND',
  });
  const typeLabel = intl.formatMessage({
    id: 'PRODUCT.LABEL.TYPE',
  });
  const priceLabel = intl.formatMessage({
    id: 'PRODUCT.LABEL.PRICE',
  });
  const countLabel = intl.formatMessage({
    id: 'PRODUCT.LABEL.COUNT',
  });
  const orderedCountLabel = intl.formatMessage({
    id: 'PRODUCT.LABEL.ORDEREDCOUNT',
  });
  const saleOrderedCountLabel = intl.formatMessage({
    id: 'PRODUCT.LABEL.SALEORDEREDCOUNT',
  });
  const isPublishLabel = intl.formatMessage({
    id: 'PRODUCT.LABEL.ISPUBLISH',
  });
  const statusLabel = intl.formatMessage({
    id: 'PRODUCT.LABEL.STATUS',
  });
  const actionsLabel = intl.formatMessage({ id: 'PRODUCT.LABEL.ACTIONS' });

  // Products UI Context
  const ProductsUIContext = useProductsUIContext();
  const ProductsUIProps = useMemo(() => {
    return {
      queryParams: ProductsUIContext.queryParams,
      setQueryParams: ProductsUIContext.setQueryParams,
      openViewProductDialog: ProductsUIContext.openViewProductDialog,
      openDeleteProductDialog: ProductsUIContext.openDeleteProductDialog,
    };
  }, [ProductsUIContext]);

  // Getting curret state of Products list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.products }),
    shallowEqual
  );
  const { totalCount, entities, listLoading } = currentState
    ? currentState
    : { totalCount: 0, entities: [], listLoading: false };

  // Products Redux state
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(actions.fetchProducts(ProductsUIProps.queryParams));
  }, [ProductsUIProps.queryParams, dispatch]);

  // Table columns
  const columns = [
    {
      dataField: 'icon',
      text: '',
      sort: false,
      style: {
        width: '65px',
      },
      formatter: columnFormatters.IconColumnFormatter,
    },
    /*     {
      dataField: 'id',
      text: productCodeLabel,
      sort: true,
      sortCaret: sortCaret,
      headerSortingClasses,
      formatter: columnFormatters.IdColumnFormatter,
    }, */
    {
      dataField: 'name',
      text: productNameLabel,
      sort: true,
      sortCaret: sortCaret,
      headerSortingClasses,
      formatter: columnFormatters.NameColumnFormatter,
    },
    /*     {
      dataField: 'typeCode',
      text: typeLabel,
      sort: true,
      sortCaret: sortCaret,
      headerSortingClasses,
    }, */
    {
      dataField: 'price',
      text: priceLabel,
      sort: true,
      sortCaret: sortCaret,
      headerSortingClasses,
      formatter: columnFormatters.PriceColumnFormatter,
    },
    {
      dataField: 'count',
      text: countLabel,
      sort: true,
      sortCaret: sortCaret,
      style: {
        width: '80px',
      },
      formatter: columnFormatters.CountColumnFormatter,
    },
    {
      dataField: 'orderedCount',
      text: orderedCountLabel,
      sort: true,
      sortCaret: sortCaret,
      style: {
        width: '80fpx',
      },
      formatter: columnFormatters.CountColumnFormatter,
    },
    {
      dataField: 'saleOrderedCount',
      text: saleOrderedCountLabel,
      sort: true,
      sortCaret: sortCaret,
      style: {
        width: '80px',
      },
      formatter: columnFormatters.CountColumnFormatter,
    },
    {
      dataField: 'typeCode',
      text: typeLabel,
      sort: true,
      sortCaret: sortCaret,
      headerSortingClasses,
      formatter: columnFormatters.TypeColumnFormatter,
    },
    {
      dataField: 'categoryName',
      text: categoryLabel,
      sort: true,
      sortCaret: sortCaret,
      headerSortingClasses,
    },
    {
      dataField: 'brandName',
      text: brandLabel,
      sort: true,
      sortCaret: sortCaret,
      headerSortingClasses,
    },
    /*     {
      dataField: 'isPublish',
      text: isPublishLabel,
      sort: true,
      sortCaret: sortCaret,
      formatter: columnFormatters.IsPublishColumnFormatter,
    }, */
    {
      dataField: 'status',
      text: statusLabel,
      sort: true,
      sortCaret: sortCaret,
      formatter: columnFormatters.StatusColumnFormatter,
    },
    {
      dataField: 'action',
      text: actionsLabel,
      formatter: columnFormatters.ActionsColumnFormatter,
      formatExtraData: {
        openViewProductDialog: ProductsUIProps.openViewProductDialog,
        openDeleteProductDialog: ProductsUIProps.openDeleteProductDialog,
      },
      classes: 'text-right pr-0',
      headerClasses: 'text-right pr-3',
      style: {
        minWidth: '100px',
      },
    },
  ];
  // Table pagination properties
  const paginationOptions = {
    custom: true,
    totalSize: totalCount,
    sizePerPageList: uiHelpers.sizePerPageList,
    sizePerPage: ProductsUIProps.queryParams.pageSize,
    page: ProductsUIProps.queryParams.pageNumber,
  };
  return (
    <>
      <PaginationProvider pagination={paginationFactory(paginationOptions)}>
        {({ paginationProps, paginationTableProps }) => {
          return (
            <Pagination
              isLoading={listLoading}
              paginationProps={paginationProps}
            >
              <BootstrapTable
                wrapperClasses='table-responsive'
                bproducted={false}
                classes='table table-head-custom table-vertical-center overflow-hidden'
                bootstrap4
                remote
                keyField='id'
                data={entities === null ? [] : entities}
                columns={columns}
                defaultSorted={uiHelpers.defaultSorted}
                onTableChange={getHandlerTableChange(
                  ProductsUIProps.setQueryParams
                )}
                useSortBy
                {...paginationTableProps}
              >
                <PleaseWaitMessage entities={entities} />
                <NoRecordsFoundMessage entities={entities} />
              </BootstrapTable>
            </Pagination>
          );
        }}
      </PaginationProvider>
    </>
  );
};

export default injectIntl(ProductsTable);
